/** Sprint 5 · Form Builder · 填寫頁（依欄位動態 render + required/length 驗證 + 送出/編輯自己填的）。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import FieldRenderer, { fieldLabel } from './components/FieldRenderer';
import {
  getWorkspaceForm, createSubmission, updateSubmission, openPrint, FormDetail,
} from './services/formBuilderService';

const FUNC = 'form_workspace';

const FormFillPage: React.FC = () => {
  const { id } = useParams();
  const fid = Number(id);
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`formBuilder.${k}`, d);
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showError, showApiError } = useMessage();
  const canPrint = hasPermission(FUNC, 'print');

  const [form, setForm] = useState<FormDetail | null>(null);
  const [data, setData] = useState<Record<string, unknown>>({});
  const [subId, setSubId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const f = await getWorkspaceForm(fid); setForm(f);
      if (f.my_submission) { setData(f.my_submission.data || {}); setSubId(f.my_submission.id); }
    } catch (err) { showApiError(err); }
  }, [fid, showApiError]);

  useEffect(() => { if (!permLoading && hasPermission(FUNC, 'read')) load(); }, [permLoading, hasPermission, load]);

  const setVal = (name: string, v: unknown) => setData((p) => ({ ...p, [name]: v }));

  const validate = (): boolean => {
    if (!form) return false;
    for (const f of form.fields) {
      if (f.field_kind === 'computed' || f.field_type === 'hidden') continue;
      const v = data[f.field_name];
      if (f.required && (v === undefined || v === '' || (Array.isArray(v) && v.length === 0))) {
        showError('ERR420005', { field: fieldLabel(f) }); return false;
      }
      if (f.length && typeof v === 'string' && v.length > f.length) {
        showError('ERR420006', { detail: `${fieldLabel(f)}` }); return false;
      }
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    try {
      if (subId) await updateSubmission(fid, subId, data);
      else { const s = await createSubmission(fid, data); setSubId(s.id); }
      showSuccess('SYS420001');
    } catch (err) { showApiError(err); }
  };

  if (permLoading) return null;
  if (!hasPermission(FUNC, 'read')) return <Box sx={{ p: 3 }}>{t('common.noPermission', '您沒有權限')}</Box>;

  const visible = (form?.fields || []).filter((f) => f.field_type !== 'hidden' && f.field_kind !== 'computed');

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      <Button size="small" onClick={() => nav('/form_workspace')}>← {tt('back', '返回工作區')}</Button>
      <Typography variant="h6" sx={{ my: 1 }}>{form?.title}</Typography>
      {form?.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{form.description}</Typography>}
      {subId && <Alert severity="info" sx={{ mb: 1 }}>{tt('editingOwn', '你已填寫過，這是編輯模式。')}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          {visible.map((f) => (
            <FieldRenderer key={f.field_name} field={f} value={data[f.field_name]} onChange={setVal} />
          ))}
          {visible.length === 0 && <Typography variant="body2" color="text.secondary">{tt('noFields', '此表單尚無欄位。')}</Typography>}
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={submit}>{subId ? tt('saveEdit', '儲存修改') : tt('send', '送出')}</Button>
        {canPrint && subId && <Button variant="outlined" startIcon={<PrintIcon />}
          onClick={() => openPrint(fid).catch(showApiError)}>{tt('print', '列印')}</Button>}
      </Stack>
    </Box>
  );
};

export default FormFillPage;
